import { HttpInterceptor } from "@angular/common/http";
import { inject, Injectable, OnDestroy, Type } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    Router,
    RoutesRecognized,
    UrlSegment,
} from "@angular/router";
import { filter, map, Subscription } from "rxjs";

import {
    HERMES_DEFAULT_INTERCEPTORS,
    HERMES_INTERCEPTORS,
} from "@hermes/utils-generic-constants";
import { LOGGER } from "@hermes/core-web-logger";

@Injectable()
export class InterceptorService implements OnDestroy {
    private router = inject(Router);
    private interceptors =
        inject<Record<string, Array<Type<HttpInterceptor>>>>(
            HERMES_INTERCEPTORS,
        );
    private defaultInterceptors = inject<Array<Type<HttpInterceptor>>>(
        HERMES_DEFAULT_INTERCEPTORS,
    );
    private logger = inject(LOGGER);

    private enabledInterceptors: Array<Type<HttpInterceptor>> = [];

    private subscription = new Subscription();

    public init(): Promise<boolean> {
        this.subscription.add(
            this.router.events
                .pipe(
                    filter((event) => event instanceof RoutesRecognized),
                    map((event) => {
                        const route = (event as RoutesRecognized).state.root
                            .children[0].children[0];
                        return this.getRawUrl(route);
                    }),
                )
                .subscribe({
                    next: (url) => {
                        this.enabledInterceptors = this.interceptors[url] ?? [];

                        // Fallback adding trailing slash to URL
                        if (this.enabledInterceptors.length === 0) {
                            this.enabledInterceptors =
                                this.interceptors[`${url}/`] ?? [];
                        }
                    },
                    error: (error) => {
                        this.logger.warn(
                            "Unable to enable domain interceptors",
                            error,
                        );
                        this.enabledInterceptors = this.defaultInterceptors;
                    },
                }),
        );
        return Promise.resolve(true);
    }

    public isEnabled<T extends HttpInterceptor>(interceptor: T): boolean {
        return this.enabledInterceptors.some(
            (key) => key.name === interceptor.constructor.name,
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * @returns the raw URL as specified in app routing module (e.g `mypath/:mypathparam1/:pathparam2`)
     */
    public getRawUrl(routeSnapshot: ActivatedRouteSnapshot): string {
        const segments: UrlSegment[] = [];

        const addSegments = (snapshot: ActivatedRouteSnapshot) => {
            if (snapshot.parent) {
                addSegments(snapshot.parent);
            }

            snapshot.url.forEach((segment, index) => {
                if (snapshot.routeConfig?.path) {
                    const pathSegments = snapshot.routeConfig.path.split("/");
                    if (pathSegments[index]) {
                        segments.push(new UrlSegment(pathSegments[index], {}));
                    }
                } else {
                    segments.push(segment);
                }
            });
        };

        addSegments(routeSnapshot);
        return `${segments.map((segment) => segment.path).join("/")}`;
    }
}
